import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useGlobalEvents } from '@/hooks/useGlobalEvents';

interface StripeStatusResponse {
  connected: boolean;
  accountId?: string;
  error?: string;
  details?: string;
}

interface StripeConnectionContextType {
  isConnected: boolean;
  accountId: string;
  isLoading: boolean;
  error: string | null;
  lastCheckTimestamp: number;
  checkConnection: (forceRefresh?: boolean) => Promise<void>;
  clearCache: () => void;
}

const CACHE_DURATION_MS = 5 * 60 * 1000;
const CACHE_KEYS = {
  connected: 'stripeConnected',
  accountId: 'stripeAccountId',
  timestamp: 'stripeStatusTimestamp',
};

// Event for when Stripe connection changes
export const dispatchStripeConnectionChanged = (isConnected: boolean) => {
  const event = new CustomEvent("stripe-connection-changed", {
    detail: { isConnected },
  });
  globalThis.dispatchEvent(event);
};

const StripeConnectionContext = createContext<StripeConnectionContextType | undefined>(undefined);

export const StripeConnectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastCheckTimestamp, setLastCheckTimestamp] = useState<number>(0);
  const [cacheCleared, setCacheCleared] = useState(false);
  const { dispatchStripeConnected } = useGlobalEvents();
  const hasInitializedRef = useRef(false);
  const isCheckingRef = useRef(false);

  // Load initial state from cache immediately
  useEffect(() => {
    const cachedConnected = localStorage.getItem(CACHE_KEYS.connected);
    const cachedAccountId = localStorage.getItem(CACHE_KEYS.accountId);
    const cacheTimestamp = localStorage.getItem(CACHE_KEYS.timestamp);
    
    if (cachedConnected !== null) {
      const now = Date.now();
      const cacheAge = cacheTimestamp ? now - parseInt(cacheTimestamp) : Infinity;
      
      // Use cache even if expired, but mark for refresh
      setIsConnected(cachedConnected === 'true');
      setAccountId(cachedAccountId || '');
      
      // If cache is fresh, stop loading immediately
      if (cacheAge < CACHE_DURATION_MS) {
        setIsLoading(false);
      }
    }
  }, []);

  // Memoize the clearCache function to prevent it from being recreated on each render
  const clearCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEYS.connected);
    localStorage.removeItem(CACHE_KEYS.accountId);
    localStorage.removeItem(CACHE_KEYS.timestamp);
    setCacheCleared(true);
  }, []);

  const checkConnection = useCallback(async (forceRefresh = false) => {
    if (isCheckingRef.current) {
      return;
    }
    
    isCheckingRef.current = true;
    setIsLoading(true);
    
    try {
      const cachedConnected = localStorage.getItem(CACHE_KEYS.connected);
      const cachedAccountId = localStorage.getItem(CACHE_KEYS.accountId);
      const cacheTimestamp = localStorage.getItem(CACHE_KEYS.timestamp);
      
      const now = Date.now();
      const cacheAge = cacheTimestamp ? now - parseInt(cacheTimestamp) : Infinity;
      const cacheValid = cacheAge < CACHE_DURATION_MS && !forceRefresh && !cacheCleared;
      
      if (cachedConnected !== null && cacheValid) {
        // Use functional update to get current state without dependency
        setIsConnected((currentIsConnected) => {
          const wasConnected = currentIsConnected;
          const nowConnected = cachedConnected === 'true';
          
          // If connection status changed, dispatch event
          if (wasConnected !== nowConnected) {
            dispatchStripeConnectionChanged(nowConnected);
          }
          
          return nowConnected;
        });
        setAccountId(cachedAccountId || '');
        setIsLoading(false);
        setLastCheckTimestamp(now);
        isCheckingRef.current = false;
        
        return;
      }

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        console.error('[Stripe Debug] Auth session error when checking Stripe connection:', 
                     sessionError || 'No session found');
        setError("Authentication required to check Stripe connection status");
        setIsConnected(false);
        setAccountId('');
        setIsLoading(false);
        isCheckingRef.current = false;
        return;
      }
      
      const { data, error } = await supabase.functions.invoke('connect-stripe', {
        body: { action: 'check_connection' },
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`
        }
      });
      
      if (error) {
        console.error('[Stripe Debug] Error checking Stripe connection:', error);
        setError(error.message);
        setIsConnected(false);
        setAccountId('');
        clearCache(); // Clear any invalid cache on error
      } else {
        const response = data as StripeStatusResponse;
        
        
        if (response.error) {
          console.error('[Stripe Debug] Stripe connection error:', response.error, response.details || 'No additional details');
          setError(response.error);
          setIsConnected(false);
          setAccountId('');
          clearCache(); // Clear cache on error
        } else {
          if (response.connected && !response.accountId) {
            console.warn('[Stripe Debug] Unexpected state: connected=true but no accountId');
          }
          
          // Use functional update to get current state without dependency
          setIsConnected((currentIsConnected) => {
            const wasConnected = currentIsConnected;
            const nowConnected = response.connected;
            
            // If connection status changed, dispatch event
            if (wasConnected !== nowConnected) {
              dispatchStripeConnectionChanged(nowConnected);
              if (nowConnected) {
                dispatchStripeConnected();
              }
            }
            
            return nowConnected;
          });
          setError(null);
          setAccountId(response.accountId || '');
          
          // Only cache valid responses
          if (response.connected && response.accountId) {
            localStorage.setItem(CACHE_KEYS.connected, response.connected.toString());
            localStorage.setItem(CACHE_KEYS.accountId, response.accountId);
            localStorage.setItem(CACHE_KEYS.timestamp, now.toString());
            setCacheCleared(false);
          } else {
            clearCache();
          }
        }
      }
    } catch (err: any) {
      console.error('[Stripe Debug] Unexpected error checking Stripe connection:', err);
      setError(err.message || 'An unexpected error occurred');
      setIsConnected(false);
      setAccountId('');
      clearCache();
    } finally {
      setIsLoading(false);
      isCheckingRef.current = false;
    }
  }, [clearCache, cacheCleared]); // Removed isConnected from dependencies

  // Store checkConnection in a ref to avoid dependency issues
  const checkConnectionRef = useRef(checkConnection);
  useEffect(() => {
    checkConnectionRef.current = checkConnection;
  }, [checkConnection]);

  useEffect(() => {
    // Only initialize once to prevent multiple instances from all checking
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    
    // On initial mount, check connection, but don't force refresh to avoid infinite loops
    checkConnectionRef.current(false); 
    
    // Listen for Stripe connection events from other parts of the app
    const handleStripeConnectionChanged = (event: any) => {
      const { isConnected: newIsConnected } = event.detail;
      // Use functional update to compare with current state
      setIsConnected((currentIsConnected) => {
        if (newIsConnected !== currentIsConnected) {
          // Force a fresh check when we get an event
          checkConnectionRef.current(true);
        }
        return currentIsConnected; // Return current state, will be updated by checkConnection
      });
    };
    
    globalThis.addEventListener('stripe-connection-changed', handleStripeConnectionChanged);
    
    return () => {
      globalThis.removeEventListener('stripe-connection-changed', handleStripeConnectionChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  return (
    <StripeConnectionContext.Provider
      value={{
        isConnected,
        accountId,
        isLoading,
        error,
        lastCheckTimestamp,
        checkConnection,
        clearCache
      }}
    >
      {children}
    </StripeConnectionContext.Provider>
  );
};

export const useStripeConnectionContext = () => {
  const context = useContext(StripeConnectionContext);
  if (context === undefined) {
    throw new Error('useStripeConnectionContext must be used within a StripeConnectionProvider');
  }
  return context;
};
